// ErrorBanner — the in-context failure surface for a pane/operation,
// pure.
//
// Where a whole surface failed but the app is usable elsewhere
// (error-handling.md §5: an Upstream on one load, a not_found of an
// opened resource), the pane shows this banner in place of its content
// — persistent until resolved, carrying the ONE recovery action the
// caller composes as a child Button (alarm discipline: one action, no
// menu of options).
//
// Presentational only: `title`/`body`/`action` are ReactNode the caller
// owns (already localized — typically from `toDisplay`'s Display); this
// primitive contributes no strings. Severity is announced structurally:
// `danger` live-announces via role="alert", the calmer tones via
// role="status" — and the hue is confined to the glyph (which also
// differs in FORM per severity), never the text (SC 1.4.1).
//
// Three parts (pen J-01 "Felruta"): what happened (`title`, `body`), what
// is kept (`kept`), what you can do (`action`). No developer info on screen
// (owner, 2026-10-22; SF-14): `errorId` is never shown; it adds one link,
// "Copy error details", which copies the error ID, version and time. The
// copy is a request to the shell (app/copy.ts), never a clipboard call here.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { useCopy } from "../../app/copy.js";
import { Icon } from "../Icon/Icon.js";
import "./ErrorBanner.css";

export type ErrorBannerSeverity = "info" | "warning" | "danger";

export type ErrorBannerProps = {
    readonly severity: ErrorBannerSeverity;
    readonly title: ReactNode;
    readonly body?: ReactNode;
    // The single recovery affordance (a Button), when one exists.
    readonly action?: ReactNode;
    // What is kept despite the failure ("Nothing has been lost ...").
    readonly kept?: ReactNode;
    // The reference code (app/errorId.ts): copied, never shown.
    readonly errorId?: string;
};

// One glyph per severity — the non-colour channel differs in shape:
// circle-i (info), triangle (warning), octagon (danger).
const glyphs: Readonly<Record<ErrorBannerSeverity, ReactNode>> = {
    info: <Icon name="info" size="lg" />,
    warning: <Icon name="warning" size="lg" />,
    danger: <Icon name="danger" size="lg" />,
};

export const ErrorBanner = ({
    severity,
    title,
    body,
    action,
    kept,
    errorId,
}: ErrorBannerProps): ReactNode => (
    <div
        className={`bilda-error-banner bilda-error-banner--${severity}`}
        role={severity === "danger" ? "alert" : "status"}
    >
        <span className="bilda-error-banner__icon" aria-hidden="true">
            {glyphs[severity]}
        </span>
        <p className="bilda-error-banner__title">{title}</p>
        {body != null && <p className="bilda-error-banner__body">{body}</p>}
        {kept != null && <p className="bilda-error-banner__body">{kept}</p>}
        {action != null && (
            <div className="bilda-error-banner__action">{action}</div>
        )}
        {errorId !== undefined && <CopyErrorDetails errorId={errorId} />}
    </div>
);

// "Copy error details": the error ID, version and time go to the clipboard,
// for a report; none of them is shown. No copy door (outside the shell): no
// link, never a fake one.
export const CopyErrorDetails = ({
    errorId,
}: {
    readonly errorId: string;
}): ReactNode => {
    const { t } = useLingui();
    const copy = useCopy();
    return copy === null ? null : (
        <p className="bilda-error-banner__reference">
            <button
                type="button"
                className="bilda-error-banner__copy"
                onClick={() => copy({ type: "error-info", errorId })}
            >
                {t`Copy error details`}
            </button>
        </p>
    );
};
